import { useQuery } from '@tanstack/react-query';
import { ProfessionalsService } from '../professional.service';
import { UseQueryOptions } from '@tanstack/react-query';
import { PublicService } from '../public.service';

export const useGetProfessionalsByRole = (
	params: Professionals.ProfessionalParams,
	options?: Omit<
		UseQueryOptions<Professionals.GetProfessionalsRespose, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professionals',
			params.search,
			params.contract,
			params.service_modality,
			params.service_id,
			params.authorized,
			params.page,
			params.limit,
		],
		queryFn: () => ProfessionalsService.getByRole(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetProfessionals = (
	params: Professionals.ProfessionalParams,
	options?: Omit<
		UseQueryOptions<Professionals.GetProfessionalsRespose, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professionals',
			params.search,
			params.contract,
			params.service_modality,
			params.service_id,
			params.authorized,
			params.permission_tags,
			params.page,
			params.limit,
		],
		queryFn: () => ProfessionalsService.get(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

/** Admin: profissionais visíveis ao usuário B2C, na ordem de exibição atual. */
export const useGetProfessionalsDisplayOrder = (
	params?: Professionals.DisplayOrderParams,
	options?: Omit<
		UseQueryOptions<Array<Professionals.DisplayOrderItem>, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professionals',
			'display-order',
			params?.service_id ?? 'all',
		],
		queryFn: () => ProfessionalsService.getDisplayOrderList(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetProfessional = (
	id: string,
	options?: Omit<
		UseQueryOptions<Professionals.Professional, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['professional', id],
		queryFn: () => ProfessionalsService.getById(id),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetMe = (
	options?: Omit<
		UseQueryOptions<Professionals.Professional, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['professional-me'],
		queryFn: () => ProfessionalsService.getMe(),
		refetchOnWindowFocus: true,
		...options,
	});
};

export const useGetProfessionalsPublic = (
	params: Professionals.ProfessionalParams,
	options?: Omit<
		UseQueryOptions<Professionals.GetProfessionalsRespose, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professionals',
			params.search,
			params.contract,
			params.service_modality,
			params.service_id,
			params.authorized,
			params.page,
			params.limit,
			params.service_id,
		],
		queryFn: () => PublicService.getProfessionals(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetAllProfessionalsPublic = (
	params: Omit<Professionals.ProfessionalParams, 'page' | 'limit'>,
	options?: Omit<
		UseQueryOptions<Professionals.GetProfessionalsRespose, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professionals',
			'all',
			params.search,
			params.contract,
			params.service_modality,
			params.service_id,
			params.authorized,
		],
		queryFn: () => PublicService.getAllProfessionalsPublic(params),
		refetchOnWindowFocus: false,
		...options,
	});
};

/** Números públicos do profissional para o painel "Conheça o profissional". */
export const useGetProfessionalShowcase = (
	id: string,
	options?: Omit<
		UseQueryOptions<Professionals.Showcase, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['professional-showcase', id],
		queryFn: () => PublicService.getProfessionalShowcase(id),
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
		...options,
	});
};

export const useGetProfessionalPublic = (
	id: string,
	options?: Omit<
		UseQueryOptions<Professionals.Professional, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['professional-public', id],
		queryFn: () => PublicService.getProfessionalPublicById(id),
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetProfessionalMonthlyUsage = (
	id: string,
	params?: Professionals.MonthlyUsageParams,
	options?: Omit<
		UseQueryOptions<Professionals.MonthlyUsageListResponse, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: [
			'professional-monthly-usage',
			id,
			params?.page,
			params?.limit,
			params?.sort,
			params?.sort_direction,
			params?.usage_year,
			params?.usage_month,
		],
		queryFn: () => ProfessionalsService.getMonthlyUsage(id, params),
		enabled: !!id,
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetProfessionalProfession = (
	options?: Omit<
		UseQueryOptions<Services.ServiceProfession[], Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['professional-profession'],
		queryFn: () => PublicService.getProfessionalProfession(),
		refetchOnWindowFocus: false,
		...options,
	});
};
